import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FiUpload, FiRefreshCw, FiArrowRight, FiDownload } from 'react-icons/fi';
import { apiUrl } from '../../config/api';
import { BULK_UPLOAD_CSV, BULK_UPLOAD_JOB_STATUS } from '../../api/contracts';
import { getApiErrorMessage } from '../../utils/errorMessage';
import JobStatusCard from './JobStatusCard';
import './Page.css';
import './BulkUpload.css';

/** CSV template: Entertainment — columns expected by the bulk-upload API when upload_type=entertainment. */
const BULK_UPLOAD_ENTERTAINMENT_HEADERS = [
  'video_name',
  'parent_categories',
  'age_groups',
  'channel_name',
  'tags',
  'categories',
  'content_formats',
  'content_types'
];
/** CSV template: Normal videos — parent_categories and categories (replacing playlist). */
const BULK_UPLOAD_VIDEOS_HEADERS = [
  'video_name',
  'parent_categories',
  'age_groups',
  'channel_name',
  'tags',
  'categories',
  'content_formats',
  'content_types'
];

const BULK_UPLOAD_ENTERTAINMENT_TEMPLATE_FILENAME = 'bulk-upload-entertainment-template.csv';
const BULK_UPLOAD_VIDEOS_TEMPLATE_FILENAME = 'bulk-upload-videos-template.csv';

function buildTemplateCsv(uploadType) {
  const isEntertainment = uploadType === 'entertainment';
  const headers = isEntertainment ? BULK_UPLOAD_ENTERTAINMENT_HEADERS : BULK_UPLOAD_VIDEOS_HEADERS;
  const headerRow = headers.join(',');
  const exampleRowEntertainment = [
    '"Sample Video Title"',
    '"kids,education"',
    '"3-6,7-10"',
    '"BabyBillion"',
    '"fun,learning"',
    '"Category A,Category B"',
    '"Licensed, Original"',
  ].join(',');
  const exampleRowVideos = [
    '"Sample Video Title"',
    '"kids,education"',
    '"3-6,7-10"',
    '"BabyBillion"',
    '"fun,learning"',
    '"Category A,Category B"',
    '"Licensed, Original"',
  ].join(',');
  const exampleRow = isEntertainment ? exampleRowEntertainment : exampleRowVideos;
  return [headerRow, exampleRow].join('\n');
}

function downloadTemplateCsv(uploadType) {
  const isEntertainment = uploadType === 'entertainment';
  const csv = buildTemplateCsv(uploadType);
  const filename = isEntertainment
    ? BULK_UPLOAD_ENTERTAINMENT_TEMPLATE_FILENAME
    : BULK_UPLOAD_VIDEOS_TEMPLATE_FILENAME;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function BulkUpload() {
  const [uploadType, setUploadType] = useState('entertainment'); // 'entertainment' | 'videos'
  // Legacy single-file state (kept intentionally commented as requested):
  // const [file, setFile] = useState(null);
  const [csvFile, setCsvFile] = useState(null);
  const [zipFile, setZipFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [jobId, setJobId] = useState('');
  const [checking, setChecking] = useState(false);
  const [checkError, setCheckError] = useState('');
  const [status, setStatus] = useState(null);
  // Legacy single-file input ref (kept intentionally commented as requested):
  // const fileInputRef = useRef(null);
  const csvFileInputRef = useRef(null);
  const zipFileInputRef = useRef(null);

  // Legacy single-file handler (kept intentionally commented as requested):
  // const handleFileChange = (e) => {
  //   const chosen = e.target.files?.[0];
  //   setFile(chosen || null);
  //   setUploadError('');
  //   setStatus(null);
  // };

  const handleCsvFileChange = (e) => {
    const chosen = e.target.files?.[0];
    setCsvFile(chosen || null);
    setUploadError('');
    setStatus(null);
  };

  const handleZipFileChange = (e) => {
    const chosen = e.target.files?.[0];
    setZipFile(chosen || null);
    setUploadError('');
    setStatus(null);
  };

  const handleUploadTypeChange = (type) => {
    setUploadType(type);
    // Legacy single-file reset (kept intentionally commented as requested):
    // setFile(null);
    setCsvFile(null);
    setZipFile(null);
    setUploadError('');
    setStatus(null);
    // if (fileInputRef.current) fileInputRef.current.value = '';
    if (csvFileInputRef.current) csvFileInputRef.current.value = '';
    if (zipFileInputRef.current) zipFileInputRef.current.value = '';
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    // Legacy single-file validation (kept intentionally commented as requested):
    // if (!file) {
    //   setUploadError('Please select a CSV file.');
    //   return;
    // }
    // if (!file.name.toLowerCase().endsWith('.csv')) {
    //   setUploadError('File must be a CSV.');
    //   return;
    // }

    if (!csvFile) {
      setUploadError('Please select a CSV file.');
      return;
    }
    if (!csvFile.name.toLowerCase().endsWith('.csv')) {
      setUploadError('CSV file must have a .csv extension.');
      return;
    }
    if (!zipFile) {
      setUploadError('Please select a ZIP file.');
      return;
    }
    if (!zipFile.name.toLowerCase().endsWith('.zip')) {
      setUploadError('ZIP file must have a .zip extension.');
      return;
    }
    setUploadError('');
    setUploading(true);
    setStatus(null);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const formData = new FormData();
      // Legacy payload (single CSV only) kept intentionally commented:
      // formData.append('file', file);
      formData.append('file', csvFile);
      formData.append('zip_file', zipFile);
      formData.append('upload_type', uploadType === 'entertainment' ? 'entertainment' : 'videos');
      const res = await fetch(apiUrl(BULK_UPLOAD_CSV.path), {
        method: BULK_UPLOAD_CSV.method,
        headers,
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setUploadError(getApiErrorMessage(data, res.status));
        return;
      }
      const payload = data.data || data;
      const id = payload.job_id || payload.jobId;
      if (id) {
        setJobId(id);
        setCheckError('');
      } else {
        setUploadError('Server did not return a job ID.');
      }
    } catch (err) {
      setUploadError(err.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleCheckStatus = async (e) => {
    e.preventDefault();
    const id = (jobId || '').trim();
    if (!id) {
      setCheckError('Enter a job ID or upload CSV + ZIP first.');
      return;
    }
    setCheckError('');
    setChecking(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(apiUrl(BULK_UPLOAD_JOB_STATUS.pathFor(id)), {
        method: BULK_UPLOAD_JOB_STATUS.method,
        headers,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setCheckError(getApiErrorMessage(data, res.status));
        setStatus(null);
        return;
      }
      setStatus(data.data || data);
    } catch (err) {
      setCheckError(err.message || 'Failed to fetch status.');
      setStatus(null);
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="page bulk-upload-page">
      <div className="bulk-upload-header">
        <h3>Bulk upload</h3>
        <p className="bulk-upload-subtitle">
          Upload both CSV and ZIP files to process in the background. Use the job ID to check progress, or view recent jobs in Job history.
        </p>
      </div>

      <div className="bulk-upload-grid">
        <section className="bulk-upload-card bulk-upload-card-upload" aria-labelledby="upload-heading">
          <h4 id="upload-heading" className="bulk-upload-card-title">
            <FiUpload className="bulk-upload-card-icon" aria-hidden />
            Upload CSV + ZIP
          </h4>
          <div className="bulk-upload-upload-type-wrap">
            <span className="bulk-upload-upload-type-label">Upload type</span>
            <div className="bulk-upload-upload-type-options" role="group" aria-label="Choose CSV type">
              <label className="bulk-upload-upload-type-option">
                <input
                  type="radio"
                  name="upload_type"
                  value="entertainment"
                  checked={uploadType === 'entertainment'}
                  onChange={() => handleUploadTypeChange('entertainment')}
                  className="bulk-upload-upload-type-radio"
                />
                <span>Entertainment</span>
              </label>
              <label className="bulk-upload-upload-type-option">
                <input
                  type="radio"
                  name="upload_type"
                  value="videos"
                  checked={uploadType === 'videos'}
                  onChange={() => handleUploadTypeChange('videos')}
                  className="bulk-upload-upload-type-radio"
                />
                <span>Normal videos</span>
              </label>
            </div>
          </div>
          <div className="bulk-upload-template-wrap">
            <button
              type="button"
              className="bulk-upload-template-btn"
              onClick={() => downloadTemplateCsv(uploadType)}
              aria-label={`Download ${uploadType === 'entertainment' ? 'entertainment' : 'normal videos'} CSV template`}
            >
              <FiDownload className="bulk-upload-template-icon" aria-hidden />
              Download {uploadType === 'entertainment' ? 'entertainment' : 'normal videos'} template
            </button>
          </div>

          <form onSubmit={handleUpload} className="bulk-upload-form">
            {/* Legacy single-file input UI (kept intentionally commented as requested):
            <div className="bulk-upload-file-wrap">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="bulk-upload-file-input"
                id="bulk-upload-file"
              />
              <label htmlFor="bulk-upload-file" className="bulk-upload-file-label">
                {file ? file.name : 'Choose CSV file'}
              </label>
            </div>
            */}
            <div className="bulk-upload-field">
              <label htmlFor="bulk-upload-file-csv" className="bulk-upload-label">
                CSV file <span className="label-required" aria-hidden="true">*</span>
              </label>
              <div className="bulk-upload-file-wrap">
                <input
                  ref={csvFileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={handleCsvFileChange}
                  className="bulk-upload-file-input"
                  id="bulk-upload-file-csv"
                />
                <label htmlFor="bulk-upload-file-csv" className="bulk-upload-file-label">
                  {csvFile ? csvFile.name : 'Choose CSV file'}
                </label>
              </div>
            </div>
            <div className="bulk-upload-field">
              <label htmlFor="bulk-upload-file-zip" className="bulk-upload-label">
                ZIP file <span className="label-required" aria-hidden="true">*</span>
              </label>
              <div className="bulk-upload-file-wrap">
                <input
                  ref={zipFileInputRef}
                  type="file"
                  accept=".zip,application/zip,application/x-zip-compressed"
                  onChange={handleZipFileChange}
                  className="bulk-upload-file-input"
                  id="bulk-upload-file-zip"
                />
                <label htmlFor="bulk-upload-file-zip" className="bulk-upload-file-label">
                  {zipFile ? zipFile.name : 'Choose ZIP file'}
                </label>
              </div>
            </div>
            {uploadError && (
              <p id="upload-error" className="bulk-upload-error" role="alert">
                {uploadError}
              </p>
            )}
            <button
              type="submit"
              className="bulk-upload-btn bulk-upload-btn-primary"
              disabled={uploading || !csvFile || !zipFile}
            >
              {uploading ? 'Uploading…' : 'Upload'}
            </button>
          </form>
          {jobId && (
            <div className="bulk-upload-job-id-wrap">
              <span className="bulk-upload-job-id-label">Job ID</span>
              <code className="bulk-upload-job-id">{jobId}</code>
            </div>
          )}
        </section>

        <section className="bulk-upload-card bulk-upload-card-status" aria-labelledby="status-heading">
          <h4 id="status-heading" className="bulk-upload-card-title">
            <FiRefreshCw className="bulk-upload-card-icon" aria-hidden />
            Check status
          </h4>
          <form onSubmit={handleCheckStatus} className="bulk-upload-form">
            <div className="bulk-upload-field">
              <label htmlFor="bulk-upload-job-id-input" className="bulk-upload-label">
                Job ID
              </label>
              <input
                id="bulk-upload-job-id-input"
                type="text"
                className="bulk-upload-input"
                placeholder="Paste job ID from upload"
                value={jobId}
                onChange={(e) => setJobId(e.target.value)}
                aria-describedby={checkError ? 'check-error' : undefined}
              />
            </div>
            {checkError && (
              <p id="check-error" className="bulk-upload-error" role="alert">
                {checkError}
              </p>
            )}
            <button
              type="submit"
              className="bulk-upload-btn bulk-upload-btn-secondary"
              disabled={checking}
            >
              {checking ? 'Checking…' : 'Check status'}
            </button>
          </form>
          {status && (
            <div className="bulk-upload-status-result">
              <JobStatusCard job={status} />
            </div>
          )}
        </section>
      </div>

      <div className="bulk-upload-footer">
        <Link to="/dashboard/cms/job-history" className="bulk-upload-link">
          View job history (last 10 uploads)
          <FiArrowRight className="bulk-upload-link-icon" aria-hidden />
        </Link>
      </div>
    </div>
  );
}

export default BulkUpload;
